import { useEffect } from 'react'
import { useRouter } from 'next/router'
import cookies from 'js-cookie'
import capitalize from 'lodash/capitalize'
import { useIntl } from 'react-intl'
import { useFreshdeskWidgetAccountLazyQuery } from '@graphql'
import { isVendorOrExpert } from '@utils/roles'

type FreshdeskWidgetConfig = {
	host: string
	token: string
	widgetId: string
	jwtAuthToken?: string
	contactProperties?: Record<string, unknown>
}

declare global {
	interface Window {
		fdWidget?: {
			init: (config: FreshdeskWidgetConfig) => void
			reInit: (config: FreshdeskWidgetConfig) => void
			destroy: () => void
			user: { clear: () => void }
		}
	}
}

const host = 'https://dodecade.freshdesk.com'
const widgetToken = process.env.NEXT_PUBLIC_FRESHDESK_WIDGET_TOKEN
const widgetId = process.env.NEXT_PUBLIC_FRESHDESK_WIDGET_ID
const SCRIPT_ID = 'Freshdesk-js-sdk'
const TOKEN_REFRESH_DELAY = 50 * 60 * 1000

const loadWidgetScript = () =>
	new Promise<void>((resolve, reject) => {
		if (window.fdWidget) {
			resolve()
			return
		}

		const script =
			(document.getElementById(SCRIPT_ID) as HTMLScriptElement | null) ??
			document.createElement('script')
		script.addEventListener('load', () => resolve())
		script.addEventListener('error', reject)
		if (script.id) return

		script.id = SCRIPT_ID
		script.async = true
		script.src = `${host}/webchat/js/widget.js`
		document.head.appendChild(script)
	})

const clearWidget = () => {
	window.fdWidget?.user.clear()
	window.fdWidget?.destroy()
}

export const FreshdeskWidget = () => {
	const { pathname } = useRouter()
	const { locale } = useIntl()
	const widgetLocale = locale.startsWith('fr') ? 'fr' : 'en'
	const currentRole = pathname.split('/')[1]
	const enabled =
		!!widgetToken &&
		!!widgetId &&
		isVendorOrExpert(currentRole) &&
		!!cookies.get(`token-${currentRole}`)

	const [fetchAccount] = useFreshdeskWidgetAccountLazyQuery({
		fetchPolicy: 'no-cache',
	})

	useEffect(() => {
		if (!enabled || !widgetToken || !widgetId) return undefined

		let cancelled = false
		let refreshInterval: ReturnType<typeof setInterval> | undefined
		const variables = { accountableType: capitalize(currentRole) }
		const fetchJwt = async () => {
			const { data } = await fetchAccount({ variables })
			return data?.viewer?.accounts?.edges?.[0]?.node
				?.freshdeskWidgetToken
		}
		const config = (
			jwtAuthToken?: string | null,
		): FreshdeskWidgetConfig => ({
			host,
			token: widgetToken,
			widgetId,
			jwtAuthToken: jwtAuthToken ?? undefined,
			contactProperties: { language: widgetLocale },
		})

		Promise.all([fetchJwt(), loadWidgetScript()])
			.then(([jwt]) => {
				if (cancelled || !jwt) return

				window.fdWidget?.init(config(jwt))
				refreshInterval = setInterval(async () => {
					const refreshedJwt = await fetchJwt().catch(() => undefined)
					if (!cancelled && refreshedJwt) {
						window.fdWidget?.reInit(config(refreshedJwt))
					}
				}, TOKEN_REFRESH_DELAY)
			})
			.catch(() => undefined)

		return () => {
			cancelled = true
			clearInterval(refreshInterval)
			clearWidget()
		}
	}, [enabled, currentRole, fetchAccount])

	return null
}
